import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../../components/Sidebar";
import Box from "@mui/material/Box";
import { ToastContainer, toast } from "react-toastify";
import { TextField, Button, MenuItem } from "@mui/material";
import { get } from "../../library/Api";
import PulseLoader from "react-spinners/PulseLoader";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

const Zero5PendingUpdates = () => {
  const [data, setData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [admin_id, setadmin_id] = useState('');
  
  // Filters
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const rowsPerPage = pageSize;

  useEffect(() => {
    var mcdata = JSON.parse(localStorage.getItem("MCData"));
    var tempadmin_id = mcdata.admin_id;
    setadmin_id(tempadmin_id);
  }, []);

  useEffect(() => {
    get('get_company_list_id').then(res => {
      const allOption = { company_id: '0001', company_name: 'All' };
      setCompanies([allOption, ...res.data.companies]);
    })
      .catch(error => {
        console.error('Error fetching companies:', error);
      });
  }, []);

  const getCompanyNameById = useCallback((id) => {
    const company = companies.find(c => c.company_id === id);
    return company ? company.company_name : '';
  }, [companies]);

  const fetchData = useCallback(() => {
    if (!admin_id) return;

    setIsLoading(true);
    
    // Build query parameters
    const params = new URLSearchParams();
    params.append('admin_id', admin_id);
    params.append('page', currentPage.toString());
    params.append('page_size', pageSize.toString());
    
    if (selectedStatus) {
      params.append('status', selectedStatus);
    }
    
    if (selectedCompany && selectedCompany !== '0001') {
      params.append('company_id', selectedCompany);
    }
    
    if (dateFrom) {
      params.append('date_from', dateFrom);
    }
    
    if (dateTo) {
      params.append('date_to', dateTo);
    }
    
    if (searchQuery) {
      params.append('search', searchQuery);
    }

    get(`superadmin/zero5/pending-updates?${params.toString()}`)
      .then((res) => {
        setData(res.data.items || []);
        setTotalCount(res.data.pagination?.total_count || 0);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching Zero5 pending updates:', error);
        setIsLoading(false);
      });
  }, [admin_id, currentPage, pageSize, selectedStatus, selectedCompany, dateFrom, dateTo, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
    setCurrentPage(1);
  };

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
    setCurrentPage(1);
  };

  const handleDateFromChange = (event) => {
    setDateFrom(event.target.value);
    setCurrentPage(1);
  };

  const handleDateToChange = (event) => {
    setDateTo(event.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    if (!admin_id) {
      toast.error('Admin ID is required', { autoClose: 2000 });
      return;
    }

    // Build query parameters (same as fetchData but without pagination)
    const params = new URLSearchParams();
    params.append('admin_id', admin_id);
    
    if (selectedStatus) {
      params.append('status', selectedStatus);
    }
    
    if (selectedCompany && selectedCompany !== '0001') {
      params.append('company_id', selectedCompany);
    }
    
    if (dateFrom) {
      params.append('date_from', dateFrom);
    }
    
    if (dateTo) {
      params.append('date_to', dateTo);
    }
    
    if (searchQuery) {
      params.append('search', searchQuery);
    }

    get(`superadmin/zero5/pending-updates/export?${params.toString()}`, {
      responseType: 'blob'
    })
      .then((response) => {
        // Create blob from response
        const blob = new Blob([response.data], {
          type: 'text/csv'
        });
        
        // Create temporary URL for the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = url;
        
        // Get filename from Content-Disposition header or use default
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'zero5_pending_updates.csv';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }
        
        link.setAttribute('download', filename);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Clean up resources
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        
        toast.success('CSV export completed successfully', { autoClose: 2000 });
      })
      .catch((error) => {
        console.error('Error exporting CSV:', error);
        toast.error('Failed to export CSV. Please try again.', { autoClose: 2000 });
      });
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / rowsPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const startIndex = (currentPage - 1) * rowsPerPage + 1;
  const endIndex = Math.min(currentPage * rowsPerPage, totalCount);
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  return (
    <React.Fragment>
      <ToastContainer />
      <Box sx={{ display: "flex" }}>
        <Sidebar />

        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <div className="App">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h3 className="heading-style" style={{
                fontSize: '1.5rem',
                fontWeight: '500'
              }}>Zero5 Pending Updates</h3>
            </div>
          </div>

          <div style={{ background: '#fff', padding: '12px 10px', borderRadius: '15px' }}>
            {/* Filters */}
            <div className="zpu-filters-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', padding: '0 24px', marginTop: '1rem', marginBottom: '1rem' }}>
              <div className="zpu-filters-left" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <TextField
                  select
                  size="small"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  label="Status"
                  variant="outlined"
                  sx={{
                    minWidth: 150,
                    maxWidth: 150,
                    flexShrink: 0,
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                      borderRadius: '30px',
                      padding: '0 14px',
                      '& .MuiSelect-select': {
                        padding: '10px 14px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      lineHeight: 'normal',
                    },
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="PENDING">PENDING</MenuItem>
                  <MenuItem value="SENT">SENT</MenuItem>
                  <MenuItem value="RESOLVED">RESOLVED</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label="Company"
                  variant="outlined"
                  sx={{
                    minWidth: 220,
                    maxWidth: 220,
                    flexShrink: 0,
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                      borderRadius: '30px',
                      padding: '0 14px',
                      '& .MuiSelect-select': {
                        padding: '10px 14px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      lineHeight: 'normal',
                    },
                  }}
                >
                  {companies.map((company) => (
                    <MenuItem key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div className="zpu-filters-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  size="small"
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  label="Date From"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    minWidth: 150,
                    maxWidth: 150,
                    flexShrink: 0,
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                      borderRadius: '30px',
                    },
                    '& .MuiInputLabel-root': {
                      lineHeight: 'normal',
                    },
                  }}
                />

                <TextField
                  type="date"
                  size="small"
                  value={dateTo}
                  onChange={handleDateToChange}
                  label="Date To"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    minWidth: 150,
                    maxWidth: 150,
                    flexShrink: 0,
                    '& .MuiOutlinedInput-root': {
                      height: '44px',
                      borderRadius: '30px',
                    },
                    '& .MuiInputLabel-root': {
                      lineHeight: 'normal',
                    },
                  }}
                />

                <div style={{ minWidth: 220, maxWidth: 280 }}>
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ 
                      width: "100%",
                      height: "44px",
                      borderRadius: "30px",
                      padding: "0 14px",
                      border: "1px solid rgba(0, 0, 0, 0.23)",
                      fontSize: "0.875rem",
                      boxSizing: "border-box"
                    }}
                    className="search-style"
                  />
                </div>

                <Button
                  variant="outlined"
                  onClick={handleExportCSV}
                  size="small"
                  sx={{
                    height: '44px',
                    borderRadius: '30px',
                    padding: '0 20px',
                    border: '1px solid #1a73cc',
                    color: '#1a73cc',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      border: '1px solid #1a73cc',
                      backgroundColor: 'rgba(26, 115, 204, 0.04)'
                    }
                  }}
                >
                  Export CSV
                </Button>
              </div>
            </div>

            {isLoading ? (
              <div
                className="table-style"
                style={{ marginLeft: "46%", marginTop: "20%" }}
              >
                <PulseLoader
                  color="#02487B"
                  height={29}
                  margin={4}
                  radius={24}
                  width={4}
                />
                <p style={{ fontWeight: "bold", color: "#02487B" }}>
                  Loading
                </p>
              </div>
            ) : (
              <>
                <TableContainer component={Paper} className="">
                  <Table aria-label="zero5 pending updates table">
                    <TableBody>
                      <TableRow
                        className={"bg-table-blue_head"}
                        sx={{ "& > *": { borderBottom: "unset" }, borderRadius: "18px" }}
                      >
                        <TableCell className={"bg-table-blue_head"} component="th" scope="row">
                          Updated On
                        </TableCell>
                        <TableCell className={"bg-table-blue_head"} component="th" scope="row">
                          Company Name
                        </TableCell>
                        <TableCell className={"bg-table-blue_head"} component="th" scope="row">
                          Customer Name
                        </TableCell>
                        <TableCell className={"bg-table-blue_head"} component="th" scope="row">
                          Entity Type
                        </TableCell>
                        <TableCell className={"bg-table-blue_head"} component="th" scope="row">
                          Field Name
                        </TableCell>
                        <TableCell className={"bg-table-blue_head"} component="th" scope="row">
                          Old Value
                        </TableCell>
                        <TableCell className={"bg-table-blue_head"} component="th" scope="row">
                          New Value
                        </TableCell>
                        <TableCell className={"bg-table-blue_head"} component="th" scope="row">
                          Updated By
                        </TableCell>
                        <TableCell className={"bg-table-blue_head"} component="th" scope="row">
                          Status
                        </TableCell>
                      </TableRow>

                      {data.map((row, index) => (
                        <TableRow key={row.id || index} sx={{ height: '23px' }}>
                          <TableCell className={index % 2 === 0 ? "white-background" : "bg-table-blue"}>
                            {row.updated_on || 'N/A'}
                          </TableCell>
                          <TableCell className={index % 2 === 0 ? "white-background" : "bg-table-blue"}>
                            {row.company_name || row.company_id || 'N/A'}
                          </TableCell>
                          <TableCell className={index % 2 === 0 ? "white-background" : "bg-table-blue"}>
                            {row.customer_name || row.customer_id || 'N/A'}
                          </TableCell>
                          <TableCell className={index % 2 === 0 ? "white-background" : "bg-table-blue"}>
                            {row.entity_type || 'N/A'}
                          </TableCell>
                          <TableCell className={index % 2 === 0 ? "white-background" : "bg-table-blue"}>
                            {row.field_name || 'N/A'}
                          </TableCell>
                          <TableCell className={index % 2 === 0 ? "white-background" : "bg-table-blue"}>
                            {row.old_value || 'N/A'}
                          </TableCell>
                          <TableCell className={index % 2 === 0 ? "white-background" : "bg-table-blue"}>
                            {row.new_value || 'N/A'}
                          </TableCell>
                          <TableCell className={index % 2 === 0 ? "white-background" : "bg-table-blue"}>
                            {row.updated_by_name || row.updated_by || 'N/A'}
                          </TableCell>
                          <TableCell className={index % 2 === 0 ? "white-background" : "bg-table-blue"}>
                            {row.status || 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between mt-3" style={{ padding: '0 24px' }}>
                  <div>
                    <Button onClick={handlePreviousPage} disabled={currentPage === 1}>
                      Previous
                    </Button>
                    <Button onClick={handleNextPage} disabled={currentPage >= totalPages}>
                      Next
                    </Button>
                  </div>
                  {totalCount > 0 && (
                    <div className="page-info">
                      {startIndex}-{endIndex} out of {totalCount}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default Zero5PendingUpdates;

